<template>
  <Transition name="fade">
    <div v-if="open" class="blur" @click="close"></div>
  </Transition>
  <Transition name="slide" @enter="slideIn" @leave="slideOut">
    <div
      v-if="open"
      ref="panel"
      class="panel"
      :class="{
        'panel--open': open,
        'panel--wide': widePanel,
      }"
      :style="panelStyles"
      :data-testid="testId"
    >
      <div ref="panel-header" class="panel__header">
        {{ heading }}
        <IconButton
          class="close-btn"
          form="transparent"
          icon="cross"
          @click="close"
        />
      </div>
      <div
        ref="panel-content"
        class="panel__content"
        :class="{ 'panel__content--no-padding': !contentPadding }"
      >
        <slot name="content" />
      </div>
      <div v-if="!!$slots.footer" ref="panel-footer" class="panel__footer">
        <slot name="footer" />
      </div>
    </div>
  </Transition>
</template>

<script>
import IconButton from "@/components/IconButton.vue";
import scrollLock from "@/helpers/scroll-lock";

export default {
  name: "SlideIn",
  components: {
    IconButton,
  },
  inject: ["mq"],
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    heading: {
      type: String,
      required: true,
    },
    desktopSide: {
      type: String,
      default: "right",
      validator(value) {
        return ["right", "left"].includes(value);
      },
    },
    contentPadding: {
      type: Boolean,
      default: true,
    },
    lockScroll: {
      type: Boolean,
      default: true,
    },
    testId: {
      type: String,
      default: null,
    },
    widePanel: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: String,
      default: null,
    },
  },
  emits: ["close"],
  computed: {
    panelStyles() {
      if (!this.maxWidth) {
        return null;
      }

      return {
        maxWidth: this.maxWidth,
      };
    },
  },
  methods: {
    close() {
      this.$emit("close", false);
    },
    slideIn(element) {
      if (this.lockScroll) {
        const siteHtml = document.getElementsByTagName("html")[0];
        // Marker class (e.g. Lookbook wheel handler); actual lock preserves scroll.
        siteHtml.classList.add("slideup-lock-scroll");
        scrollLock.enable();

        // Ensure the viewport is properly set for iOS
        const viewportMeta = document.querySelector("meta[name=viewport]");
        if (viewportMeta) {
          viewportMeta.setAttribute(
            "content",
            "width=device-width, initial-scale=1.0, viewport-fit=cover"
          );
        }
      }
      this.$nextTick(() => {
        element.style[this.desktopSide] = "auto";

        const width = getComputedStyle(element).width;
        element.style[this.desktopSide] = "-" + width;

        requestAnimationFrame(() => {
          element.style[this.desktopSide] = "0";
        });
      });
    },
    slideOut(element) {
      if (this.lockScroll) {
        const siteHtml = document.getElementsByTagName("html")[0];
        siteHtml.classList.remove("slideup-lock-scroll");
        scrollLock.disable();

        // Reset viewport meta if necessary
        const viewportMeta = document.querySelector("meta[name=viewport]");
        if (
          viewportMeta &&
          viewportMeta.getAttribute("content").includes("viewport-fit=cover")
        ) {
          viewportMeta.setAttribute(
            "content",
            "width=device-width, initial-scale=1.0"
          );
        }
      }

      element.style[this.desktopSide] = "-" + getComputedStyle(element).width;
    },
  },
};
</script>

<style lang="scss" scoped>
.panel {
  transform: translate3d(0, 0, 0);
  overflow: hidden;
  position: fixed;
  background: $color-white;
  height: 100vh;
  width: 100%;
  max-width: 390px;
  z-index: 8;
  transition-timing-function: ease-in;
  transition: left 0.5s, right 0.5s;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  left: auto;
  right: auto;
  bottom: auto;
  top: 0;

  &.panel--wide {
    @include respond-above(md) {
      max-width: 740px;
    }
  }

  /* Fix for iOS Safari */
  height: -webkit-fill-available;

  @supports (-webkit-touch-callout: none) {
    height: 100%;
    &::after {
      content: "";
      position: absolute;
      bottom: -60px;
      left: 0;
      right: 0;
      height: 60px;
      background-color: $color-white;
      z-index: -1;
    }
  }

  &--open {
    transition-timing-function: ease-out;
  }

  &__header {
    position: relative;
    padding: 16px 20px;
    flex: 0 0 auto;
    text-align: center;
    @include editorial-body(14px);

    .close-btn {
      padding: 16px 20px;
      position: absolute;
      top: 0;
      right: 0;
    }

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1px;
      background-color: $color-grey-light;
      margin: 0 $v-container-padding--mobile + 0px;

      @include respond-above(md) {
        margin: 0 $v-container-padding--desktop + 0px;
      }
    }
  }

  &__footer {
    position: relative;
    padding: 20px;
    bottom: 0;
    flex: 0 0 auto;

    /* Fix for iOS Safari address bar */
    @supports (-webkit-touch-callout: none) {
      /* Larger padding at the bottom to ensure content stays above the address bar */
      padding-bottom: calc(env(safe-area-inset-bottom, 20px) + 40px);
      /* White background that extends beyond the buttons to the bottom */
      &::before {
        content: "";
        position: absolute;
        left: 0;
        right: 0;
        top: 100%;
        height: calc(env(safe-area-inset-bottom, 20px) + 40px);
        background-color: $color-white;
        z-index: -1;
      }
    }

    &::after {
      margin: 0 20px;
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background-color: $color-grey-light;
    }
  }

  &__content {
    padding: 20px;
    overflow-y: auto;
    flex: 1 1 auto;

    &--no-padding {
      padding: 0;
    }
  }
}

.blur {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 7;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur($blur-amount);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
